import React, { memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Image } from 'expo-image';
import Svg, { Path } from 'react-native-svg';
import { Text } from '../../ui/AppText';
import { styles } from '../../../screens/CommunityScreen.styles';

const MAX_VISUAL_DEPTH = 5;
const REPLY_BUTTON_DEPTH_LIMIT = 2;

/**
 * Renderiza um comentário e recursivamente suas respostas (estilo Discord).
 *
 * Memoização: o comparador customizado evita re-render quando o nó atual e seus
 * metadados de layout (depth, isLast) não mudaram. Como cada comment é tratado
 * como referência imutável após o append otimista, isso desliga a propagação
 * de re-render entre ramos da árvore — só o ramo que recebeu uma resposta
 * realmente re-renderiza.
 */
const CommentThread = memo(({ comment, depth = 0, isLast = false, colors, onReply }) => {
  const actualDepth = Math.min(depth, MAX_VISUAL_DEPTH);
  const hasReplies = comment.replies?.length > 0;
  const canReply = actualDepth < REPLY_BUTTON_DEPTH_LIMIT;

  const handleReplyPress = useCallback(() => {
    onReply?.({ id: comment.id, author: comment.author });
  }, [onReply, comment.id, comment.author]);

  return (
    <View
      style={[
        styles.threadContainer,
        { marginLeft: actualDepth > 0 ? 20 : 0 },
      ]}
    >
      {actualDepth > 0 && (
        <View
          style={[
            styles.threadLineVertical,
            { backgroundColor: colors.border, height: isLast ? 24 : '100%' },
          ]}
        />
      )}

      {actualDepth > 0 && (
        <View style={[styles.threadLineHorizontal, { backgroundColor: colors.border }]} />
      )}

      <View style={[styles.discordComment, actualDepth > 0 && styles.threadedComment]}>
        {comment.avatarUrl ? (
          <Image
            source={{ uri: comment.avatarUrl }}
            style={[
              styles.discordAvatarImg,
              actualDepth > 0 && styles.threadedAvatarImg,
            ]}
            contentFit="cover"
            transition={200}
            cachePolicy="memory-disk"
          />
        ) : (
          <View
            style={[
              styles.discordAvatar,
              { backgroundColor: colors.bgTertiary },
              actualDepth > 0 && styles.threadedAvatar,
            ]}
          >
            <Text
              style={[
                styles.discordAvatarEmoji,
                actualDepth > 0 && { fontSize: 16 },
              ]}
            >
              {comment.avatar}
            </Text>
          </View>
        )}

        <View style={styles.discordCommentContent}>
          <View style={styles.discordCommentHeader}>
            <Text style={[styles.discordAuthorName, { color: colors.accentPrimary }]}>
              {comment.author}
            </Text>
            <Text style={[styles.discordTime, { color: colors.textTertiary }]}>
              {comment.time}
            </Text>
          </View>

          <Text style={[styles.discordMessage, { color: colors.textSecondary }]}>
            {comment.content}
          </Text>

          {canReply && (
            <TouchableOpacity
              style={styles.replyButton}
              onPress={handleReplyPress}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                <Path
                  d="M9 17L4 12L9 7"
                  stroke={colors.textTertiary}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <Path
                  d="M4 12H14C16.1217 12 18.1566 12.8429 19.6569 14.3431C21.1571 15.8434 22 17.8783 22 20V21"
                  stroke={colors.textTertiary}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Svg>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {hasReplies && (
        <View style={styles.repliesContainer}>
          {comment.replies.map((reply, idx) => (
            <CommentThread
              key={`thread-${reply.id}`}
              comment={reply}
              depth={actualDepth + 1}
              isLast={idx === comment.replies.length - 1}
              colors={colors}
              onReply={onReply}
            />
          ))}
        </View>
      )}
    </View>
  );
}, (prev, next) =>
  prev.comment === next.comment &&
  prev.depth === next.depth &&
  prev.isLast === next.isLast &&
  prev.colors === next.colors &&
  prev.onReply === next.onReply,
);

CommentThread.displayName = 'CommentThread';

export default CommentThread;
